import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import projectsService, {
  AnalysisRun,
  PrecomputedDemo,
} from "../services/projects.service";

interface Props {
  projectName: string;
  onOpen: (run: AnalysisRun, projectName: string) => void | Promise<void>;
}

const PrecomputedDemosPanel = ({ projectName, onOpen }: Props) => {
  const [name, setName] = useState("");
  const [clusterColumn, setClusterColumn] = useState("leiden");
  const [rootColumn, setRootColumn] = useState("annotation");
  const [rootValue, setRootValue] = useState("");
  const [useRep, setUseRep] = useState("X_pca");
  const [demos, setDemos] = useState<PrecomputedDemo[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setDemos(await projectsService.fetchAllPrecomputedDemos());
      setError("");
    } catch (err: any) {
      setError(err?.message || "Could not load demo results.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!demos.some((demo) => demo.status === "queued" || demo.status === "running")) return;
    const timer = window.setInterval(refresh, 4000);
    return () => window.clearInterval(timer);
  }, [demos, refresh]);

  const createDemo = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a name for these demo results.");
      return;
    }
    if (!rootValue.trim()) {
      setError("Enter the biological root population for trajectory analysis.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const demo = await projectsService.createPrecomputedDemo(projectName, trimmed, {
        cluster_column: clusterColumn.trim(),
        root_column: rootColumn.trim(),
        root_value: rootValue.trim(),
        use_rep: useRep,
      });
      setDemos((current) => [{ ...demo, project_name: demo.project_name || projectName }, ...current]);
      setName("");
      setRootValue("");
    } catch (err: any) {
      setError(err?.message || "Could not start the demo build.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Precompute the core single-cell workflow with default parameters. Your normal step-by-step workflow remains unchanged.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          label="Demo results name"
          value={name}
          inputProps={{ maxLength: 120 }}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") createDemo();
          }}
          placeholder="For example: PBMC default analysis"
        />
        <Button variant="contained" onClick={createDemo} disabled={creating || !name.trim() || !rootValue.trim()}>
          {creating ? <CircularProgress size={20} color="inherit" /> : "Precompute"}
        </Button>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
        <TextField size="small" label="Trajectory cluster column" value={clusterColumn} onChange={(event) => setClusterColumn(event.target.value)} />
        <TextField size="small" label="Root annotation column" value={rootColumn} onChange={(event) => setRootColumn(event.target.value)} />
        <TextField required size="small" label="Biological root population" value={rootValue} onChange={(event) => setRootValue(event.target.value)} helperText="Exact annotation value, for example CD4⁺ Naïve T" />
        <Select size="small" value={useRep} onChange={(event) => setUseRep(event.target.value)} aria-label="Trajectory representation">
          <MenuItem value="X_pca">X_pca representation</MenuItem>
          <MenuItem value="X_umap">X_umap representation</MenuItem>
        </Select>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Divider />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : demos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No precomputed demo results yet.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {demos.map((demo) => {
            const busy = demo.status === "queued" || demo.status === "running";
            return (
              <Box
                key={demo.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>{demo.name}</Typography>
                  <Typography variant="caption" color="primary" display="block" noWrap>
                    {demo.project_name}
                  </Typography>
                  <Typography variant="caption" color={demo.status === "failed" ? "error" : "text.secondary"}>
                    {demo.status === "ready"
                      ? "Saved results are ready"
                      : demo.status === "failed"
                        ? demo.error_message || "Build failed"
                        : `Running ${demo.current_step || "preparation"}…`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                  <Chip
                    size="small"
                    color={demo.status === "ready" ? "success" : demo.status === "failed" ? "error" : "info"}
                    label={demo.status}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || demo.status !== "ready"}
                    onClick={() => onOpen(demo.run, demo.project_name)}
                  >
                    Open results
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default PrecomputedDemosPanel;
